import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";

export function PrivacyAndTermsPage() {
  useSEO({
    title: "Privacy & Terms of Use",
    description:
      "BollyRhythm terms of use and privacy notice. Learn how we handle your data and the terms governing use of our platform.",
    canonical: "/privacy-and-terms-of-use",
  });

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="py-20 max-[991px]:py-16 max-[767px]:py-12">
                <div className="flex flex-col gap-16 max-w-[50rem] mx-auto">
                  {/* ── Terms of Use ── */}
                  <div>
                    <h1 className="heading-style-h2 text-align-center">
                      Terms of Use
                    </h1>
                    <div className="spacer-medium" />
                    <div className="flex flex-col gap-6 text-[#4a4a4a] text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-[1.5]">
                      <p className="m-0">
                        Welcome to BollyRhythm! By using our website and
                        services, you agree to these terms.
                      </p>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Using Our Site
                        </h3>
                        <p className="m-0">
                          The platform's instructional videos and materials are
                          designated for individual, non-commercial purposes
                          exclusively. Users consent to refrain from sharing or
                          reproducing materials without authorization.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Accounts
                        </h3>
                        <p className="m-0">
                          You're responsible for keeping your account information
                          secure and for any activity under your account.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Purchases &amp; Access
                        </h3>
                        <p className="m-0">
                          Tutorial access extends for four months with renewal
                          possibilities. Account credential sharing or
                          distributing instructional content beyond performance
                          groups is forbidden.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Intellectual Property
                        </h3>
                        <p className="m-0">
                          All videos, images, text, and brand assets belong to
                          BollyRhythm. Unauthorized use is strictly prohibited.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Limitation of Liability
                        </h3>
                        <p className="m-0">
                          We do our best to provide high-quality content, but
                          BollyRhythm is not liable for any direct or indirect
                          damages related to your use of our site or tutorials.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Updates to Terms
                        </h3>
                        <p className="m-0">
                          Terms may be modified periodically, and ongoing site
                          usage signifies acknowledgment of revisions.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Privacy Notice ── */}
                  <div>
                    <h2 className="heading-style-h2 text-align-center">
                      Privacy Notice
                    </h2>
                    <div className="spacer-medium" />
                    <div className="flex flex-col gap-6 text-[#4a4a4a] text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-[1.5]">
                      <p className="m-0">
                        BollyRhythm prioritizes user confidentiality through
                        comprehensive disclosure of data practices.
                      </p>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Information Collected
                        </h3>
                        <ul className="m-0 pl-6 flex flex-col gap-1 list-disc">
                          <li>
                            Personal details (name, email, payment information)
                          </li>
                          <li>Behavioral tracking across the website</li>
                          <li>Browser-based tracking mechanisms</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Data Utilization
                        </h3>
                        <p className="m-0">
                          Information supports account administration,
                          transaction processing, communications, experience
                          customization, and regulatory compliance.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Data Sharing
                        </h3>
                        <p className="m-0">
                          We never sell your personal data. Third-party
                          processors receive information solely for operational
                          support.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          User Controls
                        </h3>
                        <p className="m-0">
                          Subscribers can opt out of communications via
                          unsubscribe options; browser settings manage tracking
                          preferences.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Security
                        </h3>
                        <p className="m-0">
                          We use reasonable security measures to keep your
                          personal information safe.
                        </p>
                      </div>

                      <div>
                        <h3 className="m-0 heading-style-h5 text-[#1f0a18] mb-2">
                          Contact
                        </h3>
                        <p className="m-0">
                          For questions about our privacy practices, reach out at{" "}
                          <a
                            href="mailto:support@bollyrhythm.com"
                            className="text-[#762a5c] underline"
                          >
                            support@bollyrhythm.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default PrivacyAndTermsPage;
